import { z } from "zod";

export const PHOTOS_MAX_LIMIT = 100;

export const photoUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  photo: z.string().optional(),
});

export const photoServiceSchema = z.object({
  id: z.string(),
  title: z.string(),
  referenceId: z.string().optional(),
});

export const photoItemSchema = z.object({
  id: z.string(),
  /** Stable underlying asset id; id remains the gallery publishing-link id. */
  workPhotoId: z.string().optional(),
  /** Deduplicated gallery impressions (50% visible for one second). */
  viewCount: z.number().int().nonnegative().optional(),
  /** Deduplicated full-screen photo opens, separate from impressions. */
  openCount: z.number().int().nonnegative().optional(),
  src: z.string(),
  alt: z.string(),
  type: z.enum(["image", "video"]),
  thumbnail: z.string().optional(),
  pinned: z.boolean().optional(),
  user: photoUserSchema.optional(),
  service: photoServiceSchema.optional(),
  /** Total number of likes across all devices. */
  likeCount: z.number().int().nonnegative().optional(),
  /** Whether the requesting device has liked this photo (only set when deviceId is provided). */
  liked: z.boolean().optional(),
});

export const photosListResponseSchema = z.object({
  items: z.array(photoItemSchema),
  nextCursor: z.string().nullable(),
  total: z.number().optional(),
});

export const photosListQuerySchema = z.object({
  limit: z.number().int().positive().max(PHOTOS_MAX_LIMIT).optional(),
  cursor: z.string().optional(),
  category: z.string().optional(),
  salonId: z.string().optional(),
  pinned: z.boolean().optional(),
  /** Anonymous device identifier used to hydrate per-device `liked` state. */
  deviceId: z.string().min(1).max(128).optional(),
});

export const photoCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
});

export const photoCategoriesResponseSchema = z.object({
  items: z.array(photoCategorySchema),
});

export const photoCategoriesQuerySchema = z.object({
  salonId: z.string().min(1),
});

export type PhotoUser = z.infer<typeof photoUserSchema>;
export type PhotoService = z.infer<typeof photoServiceSchema>;
export type PhotoItem = z.infer<typeof photoItemSchema>;
export type PhotosListResponse = z.infer<typeof photosListResponseSchema>;
export type PhotosListQuery = z.infer<typeof photosListQuerySchema>;
export type PhotoCategory = z.infer<typeof photoCategorySchema>;
export type PhotoCategoriesResponse = z.infer<
  typeof photoCategoriesResponseSchema
>;
export type PhotoCategoriesQuery = z.infer<typeof photoCategoriesQuerySchema>;

// --- Likes (anonymous, device-scoped) ---

/** Max length of a client-supplied device identifier. */
export const PHOTO_DEVICE_ID_MAX = 128;

export const photoLikeBodySchema = z.object({
  /** Anonymous device identifier (e.g. a localStorage UUID). */
  deviceId: z.string().min(1).max(PHOTO_DEVICE_ID_MAX),
});

export const photoLikeResponseSchema = z.object({
  /** The SalonWorkPhoto id that was (un)liked. */
  photoId: z.string(),
  liked: z.boolean(),
  likeCount: z.number().int().nonnegative(),
});

export const photoLikesQuerySchema = z.object({
  salonId: z.string().min(1),
  deviceId: z.string().min(1).max(PHOTO_DEVICE_ID_MAX),
});

export const photoLikesResponseSchema = z.object({
  /** SalonWorkPhoto ids the device has liked within the salon. */
  likedPhotoIds: z.array(z.string()),
});

export type PhotoLikeBody = z.infer<typeof photoLikeBodySchema>;
export type PhotoLikeResponse = z.infer<typeof photoLikeResponseSchema>;
export type PhotoLikesQuery = z.infer<typeof photoLikesQuerySchema>;
export type PhotoLikesResponse = z.infer<typeof photoLikesResponseSchema>;

// --- Category CRUD (authenticated) ---

export const photoCategoryCreateSchema = z.object({
  salonId: z.string().min(1),
  name: z.string().min(1).max(60),
});

export const photoCategoryUpdateSchema = z.object({
  id: z.string().min(1),
  salonId: z.string().min(1),
  name: z.string().min(1).max(60).optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const photoCategoryDeleteSchema = z.object({
  id: z.string().min(1),
  salonId: z.string().min(1),
});

export const photoCategoryReorderSchema = z.object({
  salonId: z.string().min(1),
  /** Ordered list of category IDs — the index becomes the new sortOrder. */
  order: z.array(z.string().min(1)).min(1),
});

export const photoCategoryMutationResponseSchema = z.object({
  category: photoCategorySchema,
});

export const photoCategoryDeleteResponseSchema = z.object({
  deleted: z.boolean(),
});

export const photoCategoryReorderResponseSchema = z.object({
  items: z.array(photoCategorySchema),
});

export type PhotoCategoryCreate = z.infer<typeof photoCategoryCreateSchema>;
export type PhotoCategoryUpdate = z.infer<typeof photoCategoryUpdateSchema>;
export type PhotoCategoryDelete = z.infer<typeof photoCategoryDeleteSchema>;
export type PhotoCategoryReorder = z.infer<typeof photoCategoryReorderSchema>;
export type PhotoCategoryMutationResponse = z.infer<
  typeof photoCategoryMutationResponseSchema
>;
export type PhotoCategoryDeleteResponse = z.infer<
  typeof photoCategoryDeleteResponseSchema
>;
export type PhotoCategoryReorderResponse = z.infer<
  typeof photoCategoryReorderResponseSchema
>;

// Photo engagement: anonymous and idempotent per page visit, not per person.
export const PHOTO_VIEWS_MAX_EVENTS = 50;
export const photoViewKindSchema = z.enum(["impression", "open"]);
export const photoViewEventSchema = z.object({
  photoId: z.string().min(1).max(191),
  kind: photoViewKindSchema,
});
export const photoViewsRecordBodySchema = z.object({
  salonId: z.string().min(1).max(191),
  sessionId: z.uuid(),
  /** Optional origin for server integrations. Browser Origin takes precedence. */
  source: z
    .url()
    .max(255)
    .refine((value) => {
      try {
        const url = new URL(value);
        return (
          ["http:", "https:"].includes(url.protocol) &&
          !url.username &&
          !url.password &&
          url.pathname === "/" &&
          !url.search &&
          !url.hash
        );
      } catch {
        return false;
      }
    }, "Source must be an HTTP(S) origin, without a path or query")
    .optional(),
  events: z.array(photoViewEventSchema).min(1).max(PHOTO_VIEWS_MAX_EVENTS),
});
export const photoViewsRecordResponseSchema = z.object({
  /** Newly counted events; retries and unknown/ineligible photos add zero. */
  recorded: z.number().int().nonnegative(),
});
export type PhotoViewKind = z.infer<typeof photoViewKindSchema>;
export type PhotoViewEvent = z.infer<typeof photoViewEventSchema>;
export type PhotoViewsRecordBody = z.infer<typeof photoViewsRecordBodySchema>;
export type PhotoViewsRecordResponse = z.infer<
  typeof photoViewsRecordResponseSchema
>;
